/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type {
  Color,
  HallowElementProps,
  WithButtonWrapper,
} from "@packages/ui";
import {
  colors,
  constantSemanticColors,
  IconButton,
  numericPercentages,
  numericValues,
  Slider,
  spacing,
  useThemeValues,
  VolumeIcon,
} from "@packages/ui";
import { useCallback, useRef, useState } from "react";

import { FragmentAsChild } from "../../primitives/FragmentAsChild";

const containerStyle: Interpolation = {
  columnGap: spacing.ms,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  placeContent: "flex-start",
  placeItems: "center",
  pointerEvents: "all",
};
const containerFullStyle: Interpolation = {
  placeContent: "center",
  width: numericPercentages[100],
};
const iconStyle: Interpolation = {
  padding: numericValues[0],
};
const sliderStyle: Interpolation = {
  padding: spacing.none,
  width: spacing.xxxl,
};
const sliderFullStyle: Interpolation = {
  width: numericPercentages[100],
};

export type VolumeControlProps = WithButtonWrapper<
  Omit<HallowElementProps<"div">, "onVolumeChange"> & {
    onVolumeChange: (newVol: number) => void;
    onVolumeCommit?: (newVol: number) => void;
    value: number;
    iconColor?: Color;
    onOverlay?: boolean;
    fullWidth?: boolean;
  }
>;

export const VolumeControl = ({
  onVolumeChange,
  onVolumeCommit = () => {},
  value,
  iconColor = "white",
  onOverlay = false,
  fullWidth = false,
  Wrapper = FragmentAsChild,
  ...props
}: VolumeControlProps) => {
  const t = useTranslations();
  const [volume, setVolume] = useState<number>((value ?? 1) * 100);
  const lastVolume = useRef<number>(volume);
  const theme = useThemeValues();

  const updateVolume = useCallback(
    ([newVolume]: [number]) => {
      lastVolume.current = volume;
      onVolumeChange(newVolume / 100.0);
      setVolume(newVolume);
    },
    [volume, onVolumeChange],
  );

  const mute = useCallback(() => {
    if (volume === 0) {
      onVolumeCommit?.(lastVolume.current / 100.0);
      updateVolume([lastVolume.current]);
    } else {
      updateVolume([0]);
      onVolumeCommit?.(0);
    }
  }, [volume, updateVolume, onVolumeCommit]);

  const Icon =
    volume === 0
      ? VolumeIcon.Mute
      : volume < 50
        ? VolumeIcon.Min
        : VolumeIcon.Max;

  return (
    <div
      css={[containerStyle, fullWidth ? containerFullStyle : null]}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      <Wrapper asChild>
        <IconButton
          icon={
            <Icon
              fill={
                theme?.semanticColors?.[iconColor] ??
                colors[iconColor] ??
                constantSemanticColors[iconColor] ??
                iconColor
              }
              css={iconStyle}
            />
          }
          variant="transparentWhite"
          onClick={mute}
          title={t(
            volume === 0
              ? "web_mute_button_tooltip_unmute"
              : "web_mute_button_tooltip",
          )}
          tabIndex={0}
        />
      </Wrapper>
      <Slider
        size={"small"}
        value={volume}
        defaultValue={100}
        onValueChange={updateVolume}
        onValueCommit={([vol]) => onVolumeCommit(vol / 100.0)}
        css={[sliderStyle, fullWidth ? sliderFullStyle : null]}
        onColor={onOverlay}
      />
    </div>
  );
};
